import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  FileText,
  DollarSign,
  Trash2,
  Check,
} from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function money(n) {
  const v = Number(n || 0);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
}

// ========== OPS PULSE STRIP ==========
function OpsPulse({ ops }) {
  const navigate = useNavigate();
  if (!ops) return null;
  const parts = [
    { label: "jobs today", value: ops.total, color: "text-foreground" },
    ops.unassigned > 0 && { label: "unassigned", value: ops.unassigned, color: "text-amber-400" },
    ops.in_progress > 0 && { label: "in progress", value: ops.in_progress, color: "text-blue-400" },
    ops.completed > 0 && { label: "completed", value: ops.completed, color: "text-emerald-400" },
  ].filter(Boolean);

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-border bg-card cursor-pointer hover:bg-muted/10 transition-colors"
      onClick={() => navigate("/calendar")}
      data-testid="ops-pulse"
    >
      {parts.map((p, i) => (
        <span key={p.label} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-muted-foreground/40">·</span>}
          <span className={`text-sm font-semibold ${p.color}`}>{p.value}</span>
          <span className="text-sm text-muted-foreground">{p.label}</span>
        </span>
      ))}
      {ops.flagged > 0 && (
        <span className="flex items-center gap-1.5 ml-1">
          <span className="text-muted-foreground/40">·</span>
          <span className="inline-block w-2 h-2 rounded-sm bg-orange-400 rotate-45" />
          <span className="text-sm font-semibold text-orange-400">{ops.flagged}</span>
          <span className="text-sm text-muted-foreground">flagged</span>
        </span>
      )}
    </div>
  );
}

// ========== DOC ALERTS + FLAGGED STRIP ==========
function AlertsStrip({ docAlerts, flaggedCount }) {
  const navigate = useNavigate();
  if (!docAlerts) return null;
  const driverTotal = docAlerts.drivers_expired + docAlerts.drivers_expiring;
  const vehicleTotal = docAlerts.vehicles_expired + docAlerts.vehicles_expiring;
  const hasDocIssues = driverTotal > 0 || vehicleTotal > 0;
  const hasFlagged = flaggedCount > 0;
  if (!hasDocIssues && !hasFlagged) return null;

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="alerts-strip">
      {hasDocIssues && (
        <button
          onClick={() => navigate("/licensing")}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 transition-colors"
          data-testid="doc-alerts-btn"
        >
          <ShieldAlert className="w-4 h-4 text-red-400" />
          {docAlerts.drivers_expired > 0 && (
            <span className="text-xs font-medium text-red-400">{docAlerts.drivers_expired} driver{docAlerts.drivers_expired !== 1 ? "s" : ""} expired</span>
          )}
          {docAlerts.drivers_expiring > 0 && (
            <span className="text-xs font-medium text-amber-400">{docAlerts.drivers_expiring} expiring</span>
          )}
          {(driverTotal > 0 && vehicleTotal > 0) && <span className="text-muted-foreground/40">·</span>}
          {docAlerts.vehicles_expired > 0 && (
            <span className="text-xs font-medium text-red-400">{docAlerts.vehicles_expired} vehicle{docAlerts.vehicles_expired !== 1 ? "s" : ""} expired</span>
          )}
          {docAlerts.vehicles_expiring > 0 && (
            <span className="text-xs font-medium text-amber-400">{docAlerts.vehicles_expiring} expiring</span>
          )}
        </button>
      )}
      {hasFlagged && (
        <button
          onClick={() => navigate("/calendar")}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-orange-400/30 bg-orange-400/5 hover:bg-orange-400/10 transition-colors"
          data-testid="flagged-jobs-btn"
        >
          <span className="inline-block w-2 h-2 rounded-sm bg-orange-400 rotate-45" />
          <span className="text-xs font-medium text-orange-400">{flaggedCount} job{flaggedCount !== 1 ? "s" : ""} need attention today</span>
        </button>
      )}
    </div>
  );
}

// ========== FILING DEADLINE ROW (compact header) ==========
function DeadlineHeader({ deadline, expanded, onToggle, pastDueItems }) {
  if (!deadline) return null;
  const days = deadline.days_left;
  const dotColor = days <= 0 ? "bg-red-500" : days <= 7 ? "bg-amber-400" : days <= 14 ? "bg-amber-400/60" : "bg-emerald-400";
  const hasPastDue = pastDueItems.length > 0;

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-card hover:bg-muted/10 transition-colors text-left"
      data-testid={`deadline-${deadline.type}`}
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor} shrink-0`} />
      <span className="text-sm font-medium text-foreground flex-1">{deadline.label}</span>
      {hasPastDue && (
        <span className="text-[10px] font-semibold text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
          {pastDueItems.length} overdue
        </span>
      )}
      <span className="text-xs text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
        Due {deadline.due_date}
      </span>
      <span className={`text-xs font-semibold ${days <= 0 ? "text-red-400" : days <= 7 ? "text-amber-400" : "text-muted-foreground"}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
        {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
      </span>
      {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </button>
  );
}

// ========== BCF / SALES TAX LEDGER ==========
function TaxLedger({ type, year, isCurrentYear, onPaymentRecorded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payMonth, setPayMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/filings?type=${type}&year=${year}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [type, year]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const deletePayment = async (month, paymentId) => {
    try {
      await api.delete(`/filings/${type}/${year}/${month}/payment/${paymentId}`);
      load();
      onPaymentRecorded?.();
    } catch (e) { console.warn("Delete filing payment:", e); }
  };

  if (loading) return <div className="text-xs text-muted-foreground py-4 text-center">Loading...</div>;
  if (!data) return null;

  const label = type === "bcf" ? "NYS BCF Surcharge" : "NYS Sales Tax";

  // Reverse chronological — newest months first, but skip future months with no data
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const months = [...data.months].reverse().filter((m) => {
    if (year < now.getFullYear()) return true;
    if (year === now.getFullYear()) return m.month <= currentMonth;
    return false;
  });

  return (
    <div className="space-y-3" data-testid={`ledger-${type}`}>
      {/* YTD header */}
      <div className="flex items-center gap-4 px-3 py-2 rounded-md bg-muted/10 border border-border">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label} · {year}</span>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">Incurred</span>
        <span className="text-xs font-semibold tabular-nums" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(data.ytd_incurred)}</span>
        <span className="text-xs text-muted-foreground ml-3">Paid</span>
        <span className="text-xs font-semibold text-emerald-400 tabular-nums" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(data.ytd_paid)}</span>
        <span className="text-xs text-muted-foreground ml-3">Balance</span>
        <span className={`text-xs font-semibold tabular-nums ${data.ytd_balance > 0 ? "text-amber-400" : "text-foreground"}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(data.ytd_balance)}</span>
      </div>

      {/* Month rows */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead className="w-[100px]">Month</TableHead>
              <TableHead className="text-right">Incurred</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Date Paid</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="w-[80px]">Status</TableHead>
              {isCurrentYear && <TableHead className="w-[70px] text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.length === 0 ? (
              <TableRow><TableCell colSpan={isCurrentYear ? 8 : 7} className="text-center py-6 text-muted-foreground text-xs">No months to display.</TableCell></TableRow>
            ) : months.map((m) => {
              const totalPaid = (m.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
              const balance = Math.round((m.incurred - totalPaid) * 100) / 100;
              const isRunning = m.status === "running";
              const lastPayment = m.payments?.length > 0 ? m.payments[m.payments.length - 1] : null;
              const statusColor = {
                paid: "text-emerald-400",
                partial: "text-blue-400",
                unpaid: "text-red-400",
                running: "text-primary",
                upcoming: "text-muted-foreground/40",
                no_activity: "text-muted-foreground/30",
              }[m.status] || "text-muted-foreground";

              return (
                <TableRow
                  key={m.month}
                  className={`transition-colors ${isRunning ? "bg-primary/[0.03]" : ""} ${m.status === "unpaid" ? "border-l-2 border-l-red-500" : ""}`}
                  data-testid={`ledger-row-${type}-${m.month}`}
                >
                  <TableCell className="text-sm font-medium">
                    {isRunning && <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mr-2 animate-pulse" />}
                    {MONTH_NAMES[m.month - 1]} {year}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {m.incurred > 0 ? money(m.incurred) : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-emerald-400" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {totalPaid > 0 ? money(totalPaid) : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className={`text-right text-sm font-semibold tabular-nums ${balance > 0 ? "text-amber-400" : ""}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {m.incurred > 0 || totalPaid > 0 ? money(balance) : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {lastPayment ? lastPayment.date : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className="text-sm capitalize">
                    {lastPayment ? lastPayment.method : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium capitalize ${statusColor}`}>{m.status === "no_activity" ? "—" : m.status}</span>
                  </TableCell>
                  {isCurrentYear && (
                    <TableCell className="text-right">
                      {m.status !== "paid" && m.status !== "no_activity" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPayMonth(m)}
                          className="h-6 px-2 text-[10px] text-primary hover:bg-primary/10"
                          data-testid={`pay-btn-${type}-${m.month}`}
                        >
                          <DollarSign className="w-3 h-3 mr-0.5" /> Pay
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Payment history for expanded month — show if any month has multiple payments */}
      {months.filter((m) => (m.payments || []).length >= 1).map((m) => (
        <div key={`payments-${m.month}`} className="px-3 py-2 rounded-md bg-muted/5 border border-border space-y-1" data-testid={`payments-detail-${type}-${m.month}`}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{MONTH_NAMES[m.month - 1]} {year} — Payment History</div>
          {m.payments.map((p) => (
            <div key={p.id} className="flex items-center gap-3 text-xs">
              <span className="text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{p.date}</span>
              <span className="text-emerald-400 tabular-nums" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(p.amount)}</span>
              <span className="capitalize text-muted-foreground">{p.method}</span>
              {p.note && <span className="text-muted-foreground/60 truncate max-w-[150px]">{p.note}</span>}
              {isCurrentYear && (
                <button onClick={() => deletePayment(m.month, p.id)} className="text-red-400/60 hover:text-red-400 transition-colors ml-auto" data-testid={`delete-payment-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      ))}

      {/* Payment dialog */}
      {payMonth && (
        <PaymentDialog
          type={type}
          year={year}
          month={payMonth.month}
          monthLabel={`${MONTH_NAMES_FULL[payMonth.month - 1]} ${year}`}
          balance={Math.round((payMonth.incurred - (payMonth.payments || []).reduce((s, p) => s + (p.amount || 0), 0)) * 100) / 100}
          onClose={() => setPayMonth(null)}
          onSaved={() => { setPayMonth(null); load(); onPaymentRecorded?.(); }}
        />
      )}
    </div>
  );
}

// ========== TLC RECORDS LEDGER ==========
function TLCLedger({ year, isCurrentYear, onSubmitted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitMonth, setSubmitMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/filings?type=tlc_records&year=${year}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (loading) return <div className="text-xs text-muted-foreground py-4 text-center">Loading...</div>;
  if (!data) return null;

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const months = [...data.months].reverse().filter((m) => {
    if (year < now.getFullYear()) return true;
    if (year === now.getFullYear()) return m.month <= currentMonth;
    return false;
  });

  return (
    <div className="space-y-3" data-testid="ledger-tlc">
      <div className="flex items-center gap-4 px-3 py-2 rounded-md bg-muted/10 border border-border">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">TLC Trip Records · {year}</span>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Month</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted On</TableHead>
              {isCurrentYear && <TableHead className="w-[70px] text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.length === 0 ? (
              <TableRow><TableCell colSpan={isCurrentYear ? 4 : 3} className="text-center py-6 text-muted-foreground text-xs">No months to display.</TableCell></TableRow>
            ) : months.map((m) => {
              const isRunning = m.status === "running";
              const statusColor = {
                submitted: "text-emerald-400",
                pending: "text-amber-400",
                running: "text-primary",
                upcoming: "text-muted-foreground/40",
              }[m.status] || "text-muted-foreground";

              return (
                <TableRow
                  key={m.month}
                  className={`${isRunning ? "bg-primary/[0.03]" : ""} ${m.status === "pending" ? "border-l-2 border-l-amber-400" : ""}`}
                  data-testid={`ledger-row-tlc-${m.month}`}
                >
                  <TableCell className="text-sm font-medium">
                    {isRunning && <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mr-2 animate-pulse" />}
                    {MONTH_NAMES[m.month - 1]} {year}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium capitalize ${statusColor}`}>{m.status === "no_activity" ? "—" : m.status}</span>
                  </TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {m.submission_date || <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  {isCurrentYear && (
                    <TableCell className="text-right">
                      {m.status === "pending" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSubmitMonth(m)}
                          className="h-6 px-2 text-[10px] text-primary hover:bg-primary/10"
                          data-testid={`submit-btn-tlc-${m.month}`}
                        >
                          <Check className="w-3 h-3 mr-0.5" /> Submit
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {submitMonth && (
        <TLCSubmitDialog
          year={year}
          month={submitMonth.month}
          monthLabel={`${MONTH_NAMES_FULL[submitMonth.month - 1]} ${year}`}
          onClose={() => setSubmitMonth(null)}
          onSaved={() => { setSubmitMonth(null); load(); onSubmitted?.(); }}
        />
      )}
    </div>
  );
}

// ========== PAYMENT DIALOG ==========
function PaymentDialog({ type, year, month, monthLabel, balance, onClose, onSaved }) {
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("zelle");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setSaving(true);
    try {
      await api.post(`/filings/${type}/${year}/${month}/payment`, {
        amount: parseFloat(amount), date, method, note,
      });
      onSaved();
    } catch (e) { console.warn("Record filing payment:", e); }
    finally { setSaving(false); }
  };

  const label = type === "bcf" ? "BCF" : "Sales Tax";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-sm" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Record {label} Payment</DialogTitle>
          <DialogDescription>{monthLabel} · Balance: {money(balance)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Amount *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-muted/20 border-border pl-7" data-testid="filing-payment-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-muted/20 border-border" data-testid="filing-payment-date" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="bg-muted/20 border-border" data-testid="filing-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="zelle">Zelle</SelectItem>
                <SelectItem value="ach">ACH</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="bg-muted/20 border-border" placeholder="Optional" data-testid="filing-payment-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !amount} data-testid="filing-payment-save">{saving ? "Saving..." : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== TLC SUBMIT DIALOG ==========
function TLCSubmitDialog({ year, month, monthLabel, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/filings/tlc/${year}/${month}/submit`, { submission_date: date, notes });
      onSaved();
    } catch (e) { console.warn("TLC submit:", e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-sm" data-testid="tlc-submit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Mark TLC Records Submitted</DialogTitle>
          <DialogDescription>{monthLabel}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Submission Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-muted/20 border-border" data-testid="tlc-submit-date" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-muted/20 border-border" placeholder="Optional" data-testid="tlc-submit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tlc-submit-save">{saving ? "Saving..." : "Mark Submitted"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== MAIN DASHBOARD PAGE ==========
export default function DashboardPage() {
  const { user } = useAuth();
  const [cmd, setCmd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [expanded, setExpanded] = useState({ bcf: false, sales_tax: false, tlc_records: false });

  const currentYear = new Date().getFullYear();
  const isCurrentYear = selectedYear === currentYear;

  const loadCommand = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/command");
      setCmd(res.data);
    } catch (e) { console.error("Dashboard load error:", e); }
    finally { setLoading(false); }
  }, []);

  const loadYears = useCallback(async () => {
    try {
      const res = await api.get("/filings/years");
      setAvailableYears(res.data);
    } catch (e) { console.warn("Load filing years:", e); }
  }, []);

  useEffect(() => { loadCommand(); loadYears(); }, [loadCommand, loadYears]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const pastDueByType = useMemo(() => {
    if (!cmd) return { bcf: [], sales_tax: [], tlc_records: [] };
    const map = { bcf: [], sales_tax: [], tlc_records: [] };
    (cmd.past_due || []).forEach((pd) => {
      if (map[pd.type]) map[pd.type].push(pd);
    });
    return map;
  }, [cmd]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Command Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome back, {user?.name || user?.email}
          </p>
        </div>
      </div>

      {/* Ops Pulse */}
      <OpsPulse ops={cmd?.today_ops} />

      {/* Alerts Strip */}
      <AlertsStrip docAlerts={cmd?.doc_alerts} flaggedCount={cmd?.today_ops?.flagged || 0} />

      {/* Year Selector */}
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Filing Year</span>
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[120px] bg-card border-border h-8 text-sm" data-testid="year-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isCurrentYear && (
          <span className="text-[10px] text-muted-foreground/60 italic">Read-only archive</span>
        )}
      </div>

      {/* Filing Sections */}
      <div className="space-y-2">
        {/* BCF */}
        {cmd?.deadlines && (
          <DeadlineHeader
            deadline={cmd.deadlines.find((d) => d.type === "bcf")}
            expanded={expanded.bcf}
            onToggle={() => toggle("bcf")}
            pastDueItems={pastDueByType.bcf}
          />
        )}
        {expanded.bcf && (
          <div className="pl-5 border-l-2 border-border ml-1">
            <TaxLedger type="bcf" year={selectedYear} isCurrentYear={isCurrentYear} onPaymentRecorded={loadCommand} />
          </div>
        )}

        {/* Sales Tax */}
        {cmd?.deadlines && (
          <DeadlineHeader
            deadline={cmd.deadlines.find((d) => d.type === "sales_tax")}
            expanded={expanded.sales_tax}
            onToggle={() => toggle("sales_tax")}
            pastDueItems={pastDueByType.sales_tax}
          />
        )}
        {expanded.sales_tax && (
          <div className="pl-5 border-l-2 border-border ml-1">
            <TaxLedger type="sales_tax" year={selectedYear} isCurrentYear={isCurrentYear} onPaymentRecorded={loadCommand} />
          </div>
        )}

        {/* TLC Trip Records */}
        {cmd?.deadlines && (
          <DeadlineHeader
            deadline={cmd.deadlines.find((d) => d.type === "tlc_records")}
            expanded={expanded.tlc_records}
            onToggle={() => toggle("tlc_records")}
            pastDueItems={pastDueByType.tlc_records}
          />
        )}
        {expanded.tlc_records && (
          <div className="pl-5 border-l-2 border-border ml-1">
            <TLCLedger year={selectedYear} isCurrentYear={isCurrentYear} onSubmitted={loadCommand} />
          </div>
        )}
      </div>
    </div>
  );
}
